import { User } from '@/models/registry';
import type { User as UserType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

class UserStore implements BaseStore<User, UserType> {
  store: Map<string, User> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setUserField = <K extends keyof UserType>(
    user: User,
    key: K,
    value: UserType[K],
  ) => {
    (user as Pick<User, K>)[key] = value as User[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<UserType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.users.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.users.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.users.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({ delta }: { delta: DeltaPacketRecord<UserType> }) {
    const existingUser = this.getById(delta.record_id);
    if (!existingUser) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof UserType,
      UserType[keyof UserType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingUser[key] === newValue) {
        continue;
      }

      this.setUserField(existingUser, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingUser);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<UserType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<UserType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.users.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): User | undefined {
    return this.store.get(id);
  }

  add(input: UserType) {
    const user = new User(input);
    this.store.set(user.id, user);
  }

  // Computed getters - automatically update when store changes
  get all() {
    return Array.from(this.store.values());
  }

  get financeManagers() {
    return Array.from(this.store.values()).filter(
      (user) => user.role === 'finance_manager',
    );
  }

  get salesReps() {
    return Array.from(this.store.values()).filter(
      (user) => user.role === 'sales_rep',
    );
  }

  save(user: User) {
    this.store.delete(user.id);
    this.store.set(user.id, user);
  }

  upsert(user: UserType) {
    const existingUser = this.getById(user.id);
    if (existingUser) {
      this.replace({ id: user.id, user });
    } else {
      this.add(user);
    }
  }

  replace({ id, user }: { id: string; user: UserType }) {
    const existingUser = this.getById(id);
    if (!existingUser) return;
    existingUser.username = user.username;
    existingUser.avatar = user.avatar;
    existingUser.slack_connected = user.slack_connected;
    existingUser.role = user.role;
    existingUser.email = user.email;
    existingUser.first_name = user.first_name;
    existingUser.last_name = user.last_name;
    existingUser.slack_connected = user.slack_connected;

    this.save(existingUser);
  }

  // actions
  connectSlack(id: User['id']) {
    const user = this.getById(id);
    if (user) {
      user.slack_connected = true;
    }
  }

  updateUsername({
    id,
    username,
  }: {
    id: User['id'];
    username: User['username'];
  }) {
    const user = this.store.get(id);
    if (!user) return;
    user.username = username;
  }

  updateUserAvatar({ id, avatar }: { id: User['id']; avatar: User['avatar'] }) {
    const user = this.store.get(id);

    if (!user) return;

    user.avatar = avatar;
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'users',
      entity: 'users',
      modelStore: userStore,
    });
  }

  async hydrateDeal(userIds: User['id'][]): Promise<void> {
    userIds.forEach(async (userId) => {
      const user = await db.users.get(userId);
      if (user) this.upsert(user);
    });
  }
}

export const userStore = new UserStore();
